import { computed, ref, watch } from "vue";
import { type DateTimeFormatOptions, Settings as LuxonSettings } from "luxon";
import { merge, set } from "lodash";
import DateTimeFormat from "@/language/DateTimeFormats";
import { defaultLanguage, languageNames, type ISOLanguageKey } from "@/language";
import { hasExactDynamicTranslation, translateDynamic } from "@/language/dynamicTranslations";
import {
  getStaticAppTranslationsByPrefix,
  hasStaticAppTranslation,
  staticTranslationRevision,
  translateStaticApp,
} from "@/language/staticTranslations";

export type TranslationFunction = (key: string, ...arguments_: unknown[]) => string;
export type TranslationExistsFunction = (key: string, locale?: string) => boolean;
export type DateValue = Date | number | string;
export type DateFormat = string | (Intl.DateTimeFormatOptions & { key?: string; locale?: string });
export type NumberFormat = string | (Intl.NumberFormatOptions & { key?: string; locale?: string });

const supportedLocales = new Set<ISOLanguageKey>(
  Object.keys(languageNames).map((language) => language.replaceAll("_", "-") as ISOLanguageKey),
);

const PRIMEVUE_FIRST_DAY_OF_WEEK = {
  de: 1,
  "de-CH": 1,
  en: 0,
  "en-GB": 0,
  es: 1,
  fr: 1,
  hu: 1,
  it: 1,
  pl: 1,
  pt: 1,
  "uk-UA": 1,
} as const satisfies Record<ISOLanguageKey, 0 | 1>;

const currentLocale = ref<string>(defaultLanguage);

function normalizeLocale(locale: string | undefined): ISOLanguageKey {
  const normalized = locale?.replaceAll("_", "-") as ISOLanguageKey | undefined;
  return normalized != null && supportedLocales.has(normalized)
    ? normalized
    : (currentLocale.value as ISOLanguageKey);
}

function dateFormatOptions(format: DateFormat | undefined) {
  if (typeof format === "string") {
    return DateTimeFormat[format] as Intl.DateTimeFormatOptions | undefined;
  }
  if (format == null) return undefined;
  const { key, locale: _locale, ...options } = format;
  return key == null
    ? options
    : ({ ...(DateTimeFormat[key] ?? {}), ...options } as Intl.DateTimeFormatOptions);
}

function numberFormatOptions(format: NumberFormat | undefined) {
  if (format == null || typeof format === "string") return undefined;
  const { key: _key, locale: _locale, ...options } = format;
  return options;
}

export const t: TranslationFunction = (key, ...arguments_) => {
  void currentLocale.value;
  void staticTranslationRevision.value;
  return key.startsWith("dynamic.")
    ? translateDynamic(key)
    : translateStaticApp(key, ...arguments_);
};

export const te: TranslationExistsFunction = (key, locale) => {
  void currentLocale.value;
  void staticTranslationRevision.value;
  return key.startsWith("dynamic.")
    ? hasExactDynamicTranslation(key, locale)
    : hasStaticAppTranslation(key, locale);
};

export function d(value: DateValue, format?: DateFormat, locale?: string): string {
  const requestedLocale =
    typeof format === "object" && typeof format.locale === "string" ? format.locale : locale;
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(
    normalizeLocale(requestedLocale),
    dateFormatOptions(format),
  ).format(date);
}

export function n(value: number, format?: NumberFormat, locale?: string): string {
  const requestedLocale =
    typeof format === "object" && typeof format.locale === "string" ? format.locale : locale;
  return new Intl.NumberFormat(
    normalizeLocale(requestedLocale),
    numberFormatOptions(format),
  ).format(value);
}

export const locale = computed(() => currentLocale.value);

export function useTranslation() {
  return {
    t,
    te,
    d,
    n,
    locale,
  };
}

export function setApplicationLocale(localeOverride: string) {
  const normalizedLocale = normalizeLocale(localeOverride);
  currentLocale.value = normalizedLocale;
  LuxonSettings.defaultLocale = normalizedLocale;
  document.querySelector("html")?.setAttribute("lang", normalizedLocale);

  if (window.$primevue?.config.locale) {
    const primeVueLocale = window.$primevue.config.locale as unknown as Record<string, unknown>;
    for (const [key, value] of Object.entries(
      getStaticAppTranslationsByPrefix("primevue.", normalizedLocale),
    )) {
      const builder = {};
      set(builder, key.slice("primevue.".length), value);
      merge(primeVueLocale, builder);
    }
    primeVueLocale.firstDayOfWeek = PRIMEVUE_FIRST_DAY_OF_WEEK[normalizedLocale];
  }
  return normalizedLocale;
}

watch(staticTranslationRevision, () => {
  setApplicationLocale(currentLocale.value);
});

export const languageKeys = Object.keys(languageNames);
export const LISA_DATE_SHORT: DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
};
export const LISA_DATE_FULL: DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
};
